import { api, errorMessage, setUnauthorizedHandler } from './api';
import {
  openExportDialog,
  openEventDialog,
  openPasswordDialog,
  openSettingsDialog,
  openTemplatesDialog,
  openUploadDialog,
} from './dialogs';
import { applySettings, loadSettings } from './settings';
import { notifyChange, onChange, refreshData, state } from './state';
import { h, icon, toast, type IconName } from './ui';
import { renderCalendar } from './views/calendar';
import { renderLogin } from './views/login';
import { renderStaticPage, STATIC_PAGES } from './views/staticPage';

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Element #${id} fehlt in index.html`);
  return node as T;
}

const closeDrawer = (): void => document.body.classList.remove('drawer-open');

function currentRoute(): string {
  return location.hash.replace(/^#\/?/, '');
}

function renderSidebar(): void {
  const sb = el('sidebar');
  sb.hidden = !state.user;
  el('burger').hidden = !state.user;
  sb.replaceChildren();
  if (!state.user) return;

  const btn = (ic: IconName, label: string, action: () => void): HTMLElement =>
    h(
      'button',
      {
        class: 'nav-btn',
        type: 'button',
        title: label,
        'aria-label': label,
        onclick: () => {
          closeDrawer();
          action();
        },
      },
      icon(ic),
      h('span', { class: 'nav-label' }, label),
    );

  sb.append(
    h(
      'nav',
      { class: 'nav' },
      btn('calendar', 'Kalender', () => {
        location.hash = '#/';
        notifyChange();
      }),
      btn('list', 'Auswahltermine pflegen', openTemplatesDialog),
      btn('upload', 'ICS-Dateien hochladen', openUploadDialog),
      btn('export', 'Export nach Google Kalender', openExportDialog),
      btn('settings', 'Darstellung', openSettingsDialog),
      h('span', { class: 'spacer' }),
      btn('key', 'Passwort ändern', openPasswordDialog),
      btn('logout', 'Abmelden', () => void logout()),
    ),
  );
}

function renderUser(): void {
  const slot = el('user-slot');
  slot.replaceChildren();
  if (!state.user) return;
  slot.append(
    h('div', { class: 'user-pill', title: `Angemeldet als ${state.user}` }, h('span', { class: 'avatar' }, state.user.slice(0, 1).toUpperCase()), h('span', { class: 'user-name' }, state.user)),
  );
}

function render(): void {
  const route = currentRoute();
  const isStatic = STATIC_PAGES.includes(route);
  const main = el('main');
  renderSidebar();
  renderUser();
  el('fab').hidden = !(state.user && !isStatic);
  main.replaceChildren();
  if (isStatic) void renderStaticPage(main, route);
  else if (!state.user) renderLogin(main, onLoggedIn);
  else renderCalendar(main);
}

async function onLoggedIn(name: string): Promise<void> {
  state.user = name;
  await refreshData();
  if (currentRoute() !== '') location.hash = '#/';
  render();
}

async function logout(): Promise<void> {
  try {
    await api.logout();
  } catch (e) {
    toast(errorMessage(e), 'error');
  }
  state.user = null;
  state.events = [];
  state.templates = [];
  location.hash = '#/';
  render();
}

async function boot(): Promise<void> {
  applySettings(loadSettings());
  onChange(render);
  setUnauthorizedHandler(() => {
    if (!state.user) return;
    state.user = null;
    render();
    toast('Die Sitzung ist abgelaufen. Bitte neu anmelden.', 'error');
  });

  el('burger').addEventListener('click', () => document.body.classList.toggle('drawer-open'));
  el('overlay').addEventListener('click', closeDrawer);
  el('fab').addEventListener('click', () => openEventDialog());
  window.addEventListener('hashchange', render);

  try {
    const me = await api.me();
    state.user = me.name;
    await refreshData();
  } catch {
    state.user = null;
  }
  render();
}

void boot();
